import { useRef, useState, type ChangeEvent } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { ApiError } from '@/api/client';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RequirePermission, PermissionDenied } from '@/components/RequirePermission';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';
import type { SkuImportCommitResult, SkuImportOnExisting, SkuImportPreview } from '@/api/types';
import {
  RESTART_CODES, STATUS_KEYS, STATUS_TONE, XLSX_MIME, describeSkuImportError, describeSkuRow, downloadBase64, fileToBase64,
  precheckSkuFile, sortRowsErrorsFirst,
} from './skuImportMessages';

/**
 * Office: import a seller's product catalogue from an Excel template.
 *   template -> choose seller (required) + what to do with existing codes -> upload
 *   -> per-row check -> preview -> confirm.
 * A separate path from the CSV wizard (which stays untouched). The upload only
 * CHECKS the file; the single primary action imports exactly what was checked,
 * by batch id. Any row error refuses the whole file.
 */
export function SkuExcel({ onBack }: { onBack: () => void }): JSX.Element {
  const { t } = useLanguage();
  return (
    <RequirePermission permission="IMPORT.WRITE" fallback={<PermissionDenied reason={t('skuImport.noWrite')} />}>
      <SkuExcelInner onBack={onBack} />
    </RequirePermission>
  );
}

function SkuExcelInner({ onBack }: { onBack: () => void }): JSX.Element {
  const { t, locale } = useLanguage();
  const sellers = useApi(() => endpoints.sellers({ activeOnly: true, limit: 200 }), []);
  const [ownerId, setOwnerId] = useState('');
  const [onExisting, setOnExisting] = useState<SkuImportOnExisting>('SKIP');
  const [preview, setPreview] = useState<SkuImportPreview | null>(null);
  const [result, setResult] = useState<SkuImportCommitResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [checking, setChecking] = useState(false);
  const [committing, setCommitting] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  function startOver(): void {
    setPreview(null);
    setResult(null);
    setError(null);
  }

  async function onTemplate(): Promise<void> {
    setError(null);
    try {
      const r = await endpoints.skuImportTemplate(locale);
      downloadBase64(r.filename, r.bytesBase64, XLSX_MIME);
    } catch (e) {
      setError(e);
    }
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const input = e.target;
    const file = input.files?.[0];
    if (!file || !ownerId) return;
    setError(null);
    setPreview(null);
    const refused = precheckSkuFile(file);
    if (refused) {
      setError(refused);
      input.value = '';
      return;
    }
    setChecking(true);
    try {
      const fileBase64 = await fileToBase64(file);
      setPreview(await endpoints.skuXlsxPreview({ ownerId, fileName: file.name, onExisting, fileBase64 }));
    } catch (err) {
      setError(err);
    } finally {
      setChecking(false);
      input.value = '';
    }
  }

  async function onConfirm(): Promise<void> {
    if (!preview) return;
    setCommitting(true);
    setError(null);
    try {
      setResult(await endpoints.skuXlsxCommit(preview.batchId));
      setPreview(null);
    } catch (err) {
      setError(err);
      // The check no longer matches the catalogue (or the file itself was refused): upload again.
      if (err instanceof ApiError && RESTART_CODES.has(err.code)) setPreview(null);
    } finally {
      setCommitting(false);
    }
  }

  if (sellers.status === 'loading') return <LoadingState />;
  if (sellers.status === 'error') return <ErrorBanner error={sellers.error} onRetry={sellers.reload} />;

  const toImport = preview ? preview.totals.new + preview.totals.update : 0;
  const hasErrors = preview ? preview.totals.error > 0 : false;

  return (
    <div className="order-create">
      <div className="page-header">
        <div>
          <h1>{t('skuImport.title')}</h1>
          <div className="sub">{t('skuImport.subtitle')}</div>
        </div>
        <button type="button" onClick={onBack}>{t('skuImport.backToImports')}</button>
      </div>

      {error !== null && <div className="error-banner" role="alert">{describeSkuImportError(t, error)}</div>}

      {result ? (
        <div className="card">
          <div className="badge ok" role="status" style={{ marginBottom: 12 }}>
            {[
              result.replayed ? t('skuImport.doneReplayed') : '',
              t('skuImport.done', { created: formatNumber(result.created, locale), updated: formatNumber(result.updated, locale) }),
              result.skipped > 0 ? t('skuImport.doneSkipped', { skipped: formatNumber(result.skipped, locale) }) : '',
            ].filter(Boolean).join(' ')}
          </div>
          <div className="form-actions" style={{ flexWrap: 'wrap' }}>
            <button type="button" className="primary" onClick={startOver}>{t('skuImport.importMore')}</button>
            <button type="button" onClick={onBack}>{t('skuImport.backToImports')}</button>
          </div>
        </div>
      ) : (
        <>
          <div className="card">
            <h2 style={{ fontSize: 15, margin: '0 0 8px' }}>{t('skuImport.step1')}</h2>
            <p className="sub">{t('skuImport.templateHint')}</p>
            <div className="form-actions" style={{ flexWrap: 'wrap' }}>
              <button type="button" onClick={() => void onTemplate()}>{t('skuImport.downloadTemplate')}</button>
            </div>
          </div>

          <div className="card">
            <h2 style={{ fontSize: 15, margin: '0 0 8px' }}>{t('skuImport.step2')}</h2>
            <div className="field-row">
              <label htmlFor="sku-seller">{t('skuImport.seller')}</label>
              <select id="sku-seller" value={ownerId} onChange={(e) => { setOwnerId(e.target.value); setPreview(null); }}>
                <option value="">{t('skuImport.chooseSeller')}</option>
                {sellers.data.rows.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="sub" style={{ padding: 0, marginBottom: 4 }}>{t('skuImport.onExisting')}</legend>
              {(['SKIP', 'UPDATE'] as const).map((v) => (
                <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44 }}>
                  <input
                    type="radio"
                    name="sku-on-existing"
                    value={v}
                    checked={onExisting === v}
                    style={{ minHeight: 20, width: 20, height: 20, flex: '0 0 auto' }}
                    onChange={() => { setOnExisting(v); setPreview(null); }}
                  />
                  {t(v === 'SKIP' ? 'skuImport.onExisting.skip' : 'skuImport.onExisting.update')}
                </label>
              ))}
            </fieldset>
          </div>

          <div className="card">
            <h2 style={{ fontSize: 15, margin: '0 0 8px' }}>{t('skuImport.step3')}</h2>
            <p className="sub">{ownerId ? t('skuImport.fileHint') : t('skuImport.sellerRequired')}</p>
            <div className="form-actions" style={{ flexWrap: 'wrap' }}>
              <button type="button" disabled={checking || !ownerId} onClick={() => fileInput.current?.click()}>
                {checking ? t('skuImport.checking') : t('skuImport.upload')}
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                aria-label={t('skuImport.upload')}
                onChange={(e) => void onFile(e)}
              />
            </div>
          </div>

          {preview && (
            <div className="card" aria-label={t('skuImport.previewTitle')}>
              <h2 style={{ fontSize: 15, margin: '0 0 8px' }}>{t('skuImport.previewTitle')}</h2>
              <p className="sub">{preview.owner.name}</p>
              {preview.alreadyImported ? (
                <div className="notice" role="status">{t('skuImport.alreadyImported')}</div>
              ) : (
                <>
                  <div className={hasErrors ? 'error-banner' : 'notice'} role={hasErrors ? 'alert' : 'status'}>
                    {t('skuImport.summary', {
                      new: formatNumber(preview.totals.new, locale),
                      update: formatNumber(preview.totals.update, locale),
                      skip: formatNumber(preview.totals.skip, locale),
                      error: formatNumber(preview.totals.error, locale),
                    })}
                  </div>
                  <p className="sub">{hasErrors ? t('skuImport.hasErrors') : t('skuImport.nothingSaved')}</p>
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('skuImport.col.row')}</th>
                          <th>{t('skuImport.col.code')}</th>
                          <th>{t('skuImport.col.name')}</th>
                          <th>{t('skuImport.col.barcode')}</th>
                          <th>{t('skuImport.col.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortRowsErrorsFirst(preview.rows).map((r) => {
                          const message = describeSkuRow(t, r);
                          return (
                            <tr key={r.rowNumber}>
                              <td>{r.rowNumber}</td>
                              <td>{r.code}</td>
                              <td>{r.name}</td>
                              <td>{r.barcode}</td>
                              <td>
                                <span className={`badge ${STATUS_TONE[r.status]}`}>{t(STATUS_KEYS[r.status])}</span>
                                {message && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{message}</div>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <div className="form-actions" style={{ flexWrap: 'wrap' }}>
                {!preview.alreadyImported && (
                  <button
                    type="button"
                    className="primary"
                    disabled={committing || hasErrors || !preview.canCommit}
                    onClick={() => void onConfirm()}
                  >
                    {committing ? t('skuImport.importing') : t('skuImport.import', { n: formatNumber(toImport, locale) })}
                  </button>
                )}
                <button type="button" disabled={committing} onClick={startOver}>{t('skuImport.uploadAnother')}</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
